import { useState, useEffect, useCallback } from "react";
import Select, { MultiValue, StylesConfig } from "react-select";
import { Option } from "../../interface/Option";
import { ActivityCustomAxiosInstance } from "../../Network/ActivityCustom";

type CustomGroupOption = Option & {
  is_active?: boolean | string | number;
  status?: string;
  is_deleted?: boolean;
};

const isInactive = (option: CustomGroupOption): boolean => {
  if (option.is_active !== undefined && option.is_active !== null) {
    return option.is_active === false || option.is_active === 0 ||
      String(option.is_active).trim().toLowerCase() === "false" ||
      String(option.is_active).trim() === "0";
  }
  return String(option.status ?? (option as any).Status ?? "").trim().toLowerCase() === "inactive";
};

const ListOfActivityCustom = ({ onChange, clear, defaultValue }: any) => {
  const [options, setOptions] = useState<CustomGroupOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<CustomGroupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const loadOptions = useCallback(async () => {
    if (inputValue.trim().length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await ActivityCustomAxiosInstance.get("/getActivityCustomGroup/", {
        params: { name: inputValue, active: true },
      });
      const data = Array.isArray(response.data?.result) ? response.data.result : [];
      const formattedOptions: CustomGroupOption[] = data.map((item: any) => ({
        value: String(item.id),
        label: `(${item.id}) ${item.name}`,
        is_active: item.is_active,
        status: item.status ?? item.Status,
        is_deleted: item.is_deleted,
      }));
      setOptions(formattedOptions.filter(option => !isInactive(option) && !option.is_deleted));
    } catch (error) {
      console.error("Error loading activity custom groups:", error);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  useEffect(() => {
    const debounceTimeout = setTimeout(loadOptions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [loadOptions]);

  const handleChange = (selected: MultiValue<CustomGroupOption>) => {
    const proposed = [...selected].filter(option => !isInactive(option) && !option.is_deleted);
    const locked = selectedOptions.filter(option => isInactive(option));
    const next = [...proposed, ...locked.filter(old => !proposed.some(option => String(option.value) === String(old.value)))];
    setSelectedOptions(next);
    onChange(next);
  };

  useEffect(() => {
    if (clear) setSelectedOptions(previous => previous.filter(isInactive));
  }, [clear]);

  useEffect(() => {
    if (defaultValue) setSelectedOptions(defaultValue);
  }, [defaultValue]);

  const customStyles: StylesConfig<CustomGroupOption, true> = {
    multiValue: (base, { data }) => ({
      ...base,
      backgroundColor: isInactive(data) ? "#cbd5e1" : "#007BFF",
      border: isInactive(data) ? "1px solid #cbd5e1" : "1px solid transparent",
      borderRadius: "5px",
    }),
    multiValueLabel: (base, { data }) => ({
      ...base,
      color: isInactive(data) ? "#334155" : "#ffffff",
      fontWeight: 600,
    }),
    multiValueRemove: (base, { data }) => ({
      ...base,
      display: isInactive(data) ? "none" : base.display,
      color: isInactive(data) ? "#334155" : "#ffffff",
      ":hover": {
        backgroundColor: isInactive(data) ? "#cbd5e1" : "#0056b3",
        color: isInactive(data) ? "#334155" : "#ffffff",
      },
    }),
    option: (base, { data, isSelected, isFocused }) => ({
      ...base,
      backgroundColor: isInactive(data)
        ? isFocused ? "#e2e8f0" : "#f1f5f9"
        : isSelected ? "#007BFF" : isFocused ? "#eaf3ff" : "#ffffff",
      color: isInactive(data) ? "#64748b" : isSelected ? "#ffffff" : "#000000",
      cursor: "pointer",
    }),
    menu: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuList: (base) => ({
      ...base,
      maxHeight: 280,
      overflowY: "auto",
      pointerEvents: "auto",
      WebkitOverflowScrolling: "touch",
    }),
  };

  return (
    <Select<CustomGroupOption, true>
      menuPosition="absolute"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      captureMenuScroll={true}
      isMulti
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      isOptionDisabled={isInactive}
      isLoading={loading}
      getOptionLabel={(option) => option.label}
      onInputChange={(value, actionMeta) => {
        if (actionMeta.action === "input-change") setInputValue(value);
        return value;
      }}
      placeholder="Type at least 3 letters to search..."
      styles={customStyles}
    />
  );
};

export default ListOfActivityCustom;
